import { useEffect, useState, type FormEvent, type ReactElement } from "react";
import { Button, Modal } from "../../_common";
import {
  useApplyProviderIdentityMapping,
  usePreviewProviderIdentityMapping,
  type ProviderIdentityMappingPreview,
} from "../../symbol";

export interface ProviderIdentityTarget {
  id: string;
  ticker: string;
}

export function ProviderIdentityModal({
  portfolioId,
  target,
  onClose,
}: {
  portfolioId: string;
  target: ProviderIdentityTarget | null;
  onClose: () => void;
}): ReactElement {
  const [providerCode, setProviderCode] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState<ProviderIdentityMappingPreview | null>(null);
  const previewRequest = usePreviewProviderIdentityMapping();
  const applyMapping = useApplyProviderIdentityMapping(portfolioId);

  useEffect(() => {
    if (!target) return;
    setProviderCode(target.ticker);
    setEffectiveDate(new Date().toISOString().slice(0, 10));
    setPreview(null);
    previewRequest.reset();
    applyMapping.reset();
  }, [target]);

  const request = () => ({ portfolioId, symbolId: target!.id, providerCode: providerCode.trim(), effectiveDate });
  const runPreview = async () => {
    if (!target) return;
    try {
      const result = await previewRequest.mutateAsync(request());
      setPreview(result);
    } catch {
      setPreview(null);
    }
  };
  const handlePreview = (event: FormEvent) => {
    event.preventDefault();
    void runPreview();
  };
  const handleApply = async () => {
    if (!target || !preview?.canApply) return;
    try {
      await applyMapping.mutateAsync({
        ...request(),
        expectedMarketPriceRevision: preview.expectedMarketPriceRevision,
        expectedIdentityFingerprint: preview.expectedIdentityFingerprint,
        affectedPortfolioIds: preview.affectedPortfolioIds,
      });
      onClose();
    } catch {
      // The mutation error remains visible until the user requests a new preview.
    }
  };

  return (
    <Modal open={target !== null} onClose={onClose} title={`Correct FMP ticker · ${target?.ticker ?? ""}`}>
      <p className="mb-3 text-sm text-muted-foreground">
        This changes the FMP ticker assigned to this security across every portfolio. The portfolio ledger stays unchanged.
      </p>
      <form onSubmit={(event) => { void handlePreview(event); }} className="flex flex-col gap-3">
        <label className="text-sm font-medium" htmlFor="fmp-provider-code">FMP ticker</label>
        <input id="fmp-provider-code" className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          required value={providerCode} onChange={(event) => { setProviderCode(event.target.value); setPreview(null); }} />
        <label className="text-sm font-medium" htmlFor="fmp-effective-date">Use this ticker from</label>
        <input id="fmp-effective-date" className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          type="date" required value={effectiveDate} onChange={(event) => { setEffectiveDate(event.target.value); setPreview(null); }} />
        {(previewRequest.error || applyMapping.error) && <p role="alert" className="text-sm text-destructive">{(previewRequest.error ?? applyMapping.error)?.message}</p>}
        {preview && (
          <section className="rounded-md border p-3 text-sm" aria-live="polite">
            <h3 className="font-medium">FMP record</h3>
            <p>{preview.provider.name ?? preview.provider.code} · {preview.provider.exchange ?? "exchange unavailable"} · {preview.provider.currency ?? "currency unavailable"}</p>
            <p>ISIN: {preview.provider.isin ?? "not provided"}; CUSIP: {preview.provider.cusip ?? "not provided"}</p>
            <p>Also affects {Math.max(0, preview.affectedPortfolioIds.length - 1)} other portfolio(s).</p>
            {preview.alreadyApplied && <p>This ticker is already assigned from this date.</p>}
            {!preview.canApply && <p role="alert" className="text-destructive">Cannot confirm this ticker: {preview.reason ?? "FMP identifiers did not match"}.</p>}
          </section>
        )}
        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          {!preview && <Button type="submit" disabled={previewRequest.isPending}>{previewRequest.isPending ? "Checking FMP..." : "Check ticker"}</Button>}
          {preview && <>
            <Button type="button" variant="outline" disabled={previewRequest.isPending} onClick={() => { setPreview(null); void runPreview(); }}>Check again</Button>
            <Button type="button" disabled={!preview.canApply || preview.alreadyApplied || applyMapping.isPending} onClick={() => { void handleApply(); }}>
              {applyMapping.isPending ? "Applying..." : preview.alreadyApplied ? "Already applied" : "Apply ticker"}
            </Button>
          </>}
        </div>
      </form>
    </Modal>
  );
}
